import { inject, Injectable } from '@angular/core';

import { StripeProvider } from 'src/providers/stripe/stripe-provider';

import { Util } from 'src/shared/util/util';

import { FinancialConnectionsAccount, FinancialConnectionsService } from './financial-connections.service';

const POLL_ATTEMPTS = 6;
const POLL_DELAY_MS = 1500;

interface StripeLinkedAccount {
  id: string;
  institution_name?: string;
  last4?: string;
}

export interface BankConnectResult {
  /** False when the customer closed the Stripe modal without linking. */
  linked: boolean;
  /** The TIDY connections for the accounts Stripe just linked (new or already on file). */
  linkedConnections: FinancialConnectionsAccount[];
  /** Accounts Stripe linked that never showed up as a TIDY connection. */
  missingAccounts: StripeLinkedAccount[];
}

/**
 * Runs the Stripe Financial Connections modal and finds the TIDY connections for
 * the accounts it linked. The connection rows come from a webhook, so they can
 * lag the modal; and the webhook skips accounts TIDY already has on file, so a
 * re-linked account resolves to its existing row rather than a new one.
 */
@Injectable({ providedIn: 'root' })
export class BankConnectFlowService {
  private readonly financialConnections = inject(FinancialConnectionsService);
  private readonly stripeProvider = inject(StripeProvider);
  private readonly util = inject(Util);

  async connectBank(): Promise<BankConnectResult> {
    const before = await this.financialConnections.getConnectedAccounts().catch(() => [] as FinancialConnectionsAccount[]);
    const knownIds = new Set(before.map(account => account.id));
    const session = await this.financialConnections.createSession();
    const result = await this.stripeProvider.collectFinancialConnectionsAccounts(session.client_secret);
    if (result.error) {
      if (result.error.code !== 'session_cancelled') {
        this.util.showError(result.error.message || 'Failed to connect bank account.');
      }
      return { linked: false, linkedConnections: [], missingAccounts: [] };
    }
    const stripeAccounts: StripeLinkedAccount[] = result.financialConnectionsSession?.accounts ?? [];
    return this.resolveLinkedConnections(knownIds, stripeAccounts);
  }

  private async resolveLinkedConnections(knownIds: Set<number>, stripeAccounts: StripeLinkedAccount[]): Promise<BankConnectResult> {
    let linked: FinancialConnectionsAccount[] = [];
    let accounts: FinancialConnectionsAccount[] = [];
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
      accounts = await this.financialConnections.getConnectedAccounts().catch(() => [] as FinancialConnectionsAccount[]);
      const fresh = accounts.filter(account => !knownIds.has(account.id) && account.status === 'active');
      const matched = accounts.filter(account => account.status === 'active' && this.matchesStripeAccount(account, stripeAccounts));
      linked = this.uniqueById([...fresh, ...matched]);
      // Every linked account resolved, or nothing to match against and something new arrived.
      if (linked.length >= stripeAccounts.length && linked.length > 0) { break; }
      await new Promise(resolve => setTimeout(resolve, POLL_DELAY_MS));
    }
    const missingAccounts = stripeAccounts.filter(stripeAccount =>
      !linked.some(connection => this.matchesStripeAccount(connection, [stripeAccount]))
    );
    return { linked: true, linkedConnections: linked, missingAccounts };
  }

  private matchesStripeAccount(connection: FinancialConnectionsAccount, stripeAccounts: StripeLinkedAccount[]): boolean {
    return stripeAccounts.some(stripeAccount =>
      !!stripeAccount.last4 && stripeAccount.last4 === connection.last4 &&
      (!stripeAccount.institution_name || stripeAccount.institution_name === connection.institution_name)
    );
  }

  private uniqueById(accounts: FinancialConnectionsAccount[]): FinancialConnectionsAccount[] {
    const seen = new Set<number>();
    return accounts.filter(account => {
      if (seen.has(account.id)) { return false; }
      seen.add(account.id);
      return true;
    });
  }
}
